import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Handshake,
  RefreshCw,
  Send,
  X,
  Users,
  ExternalLink,
  Sparkles,
  Check,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "sent", label: "Sent" },
  { key: "skipped", label: "Skipped" },
  { key: "all", label: "All" },
];

function statusOf(c) {
  if (c.warm_intro_sent_at) return "sent";
  if (c.warm_intro_skipped_at) return "skipped";
  return "pending";
}

export default function WarmIntros() {
  const [candidates, setCandidates] = useState([]);
  const [networkSize, setNetworkSize] = useState(0);
  const [networkFetchedAt, setNetworkFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingNet, setRefreshingNet] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null); // lead_id currently being edited
  const [drafts, setDrafts] = useState({}); // {lead_id: {message, mutual_provider_id}}
  const [sendingId, setSendingId] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get("/warm-intros/candidates", { params: { limit: 30 } })
      .then((r) => {
        setCandidates(r.data.candidates || []);
        setNetworkSize(r.data.network_size || 0);
        setNetworkFetchedAt(r.data.network_fetched_at || null);
        // Seed drafts with the server-cached AI draft + best mutual
        const seed = {};
        (r.data.candidates || []).forEach((c) => {
          const best = (c.mutuals && c.mutuals[0]) || null;
          seed[c.lead_id] = {
            message: c.warm_intro_draft || "",
            mutual_provider_id:
              c.warm_intro_mutual_id ||
              (best ? best.provider_id : null),
          };
        });
        setDrafts(seed);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load warm-intro candidates";
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const refreshNetwork = async () => {
    setRefreshingNet(true);
    try {
      const r = await api.post("/warm-intros/refresh-network");
      toast.success(`Synced ${r.data.count} LinkedIn connections`);
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to sync LinkedIn network"
      );
    } finally {
      setRefreshingNet(false);
    }
  };

  const setDraft = (lead_id, patch) =>
    setDrafts((prev) => ({
      ...prev,
      [lead_id]: { ...(prev[lead_id] || {}), ...patch },
    }));

  const sendIntro = async (c) => {
    const d = drafts[c.lead_id] || {};
    if (!d.mutual_provider_id) {
      toast.error("Pick a mutual to route the intro through");
      return;
    }
    if (!d.message || d.message.trim().length < 20) {
      toast.error("Message is too short");
      return;
    }
    setSendingId(c.lead_id);
    try {
      await api.post("/warm-intros/send", {
        lead_id: c.lead_id,
        mutual_provider_id: d.mutual_provider_id,
        message: d.message.trim(),
      });
      toast.success("Warm intro request sent");
      // Optimistic update
      setCandidates((prev) =>
        prev.map((x) =>
          x.lead_id === c.lead_id
            ? {
                ...x,
                warm_intro_sent_at: new Date().toISOString(),
                warm_intro_mutual_id: d.mutual_provider_id,
              }
            : x
        )
      );
      setSelected(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to send warm intro"
      );
    } finally {
      setSendingId(null);
    }
  };

  const skip = async (c) => {
    try {
      await api.post("/warm-intros/skip", { lead_id: c.lead_id });
      toast.success("Skipped");
      setCandidates((prev) =>
        prev.map((x) =>
          x.lead_id === c.lead_id
            ? { ...x, warm_intro_skipped_at: new Date().toISOString() }
            : x
        )
      );
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to skip");
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return candidates;
    return candidates.filter((c) => statusOf(c) === filter);
  }, [candidates, filter]);

  const netFetchLabel = networkFetchedAt
    ? new Date(networkFetchedAt).toLocaleString()
    : "never";

  return (
    <Layout
      subtitle="AU / NZ only · manual send"
      title="Warm intros"
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshNetwork}
            disabled={refreshingNet}
            data-testid="warm-intros-refresh-network"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 mr-2 ${
                refreshingNet ? "animate-spin" : ""
              }`}
              strokeWidth={1.5}
            />
            Sync LinkedIn network
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="warm-intros-reload"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 mr-2 ${loading ? "animate-spin" : ""}`}
              strokeWidth={1.5}
            />
            Reload
          </Button>
        </>
      }
    >
      <div
        className="mb-6 flex items-center justify-between gap-3 flex-wrap"
        data-testid="warm-intros-header"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`warm-intros-filter-${f.key}`}
              className={`px-3 py-1.5 text-xs border transition-colors ${
                filter === f.key
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:text-foreground"
              }`}
              style={{ borderRadius: 2 }}
            >
              {f.label}
              {" · "}
              {f.key === "all"
                ? candidates.length
                : candidates.filter((c) => statusOf(c) === f.key).length}
            </button>
          ))}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
          {networkSize} 1st-degree connections cached
          <span className="mx-1">·</span>
          synced {netFetchLabel}
        </div>
      </div>

      {error && (
        <div
          className="mb-6 p-4 border border-destructive/40 bg-destructive/5 text-destructive text-sm"
          style={{ borderRadius: 2 }}
          data-testid="warm-intros-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          className="p-10 border border-dashed border-border text-center"
          style={{ borderRadius: 2 }}
          data-testid="warm-intros-empty"
        >
          <Handshake
            className="h-8 w-8 mx-auto mb-3 text-muted-foreground"
            strokeWidth={1.5}
          />
          <div className="text-sm text-muted-foreground">
            {filter === "pending"
              ? "No warm-intro candidates right now. Try syncing your LinkedIn network above, or check back after Apollo imports more AU/NZ CFOs."
              : `No ${filter} candidates.`}
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="warm-intros-list">
          {filtered.map((c) => {
            const status = statusOf(c);
            const d = drafts[c.lead_id] || {};
            const isOpen = selected === c.lead_id;
            const chosenMutual = c.mutuals.find(
              (m) => m.provider_id === d.mutual_provider_id
            );
            return (
              <div
                key={c.lead_id}
                className="border border-border bg-card"
                style={{ borderRadius: 2 }}
                data-testid={`warm-intro-card-${c.lead_id}`}
              >
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-sm">
                        {c.name || "(unnamed)"}
                      </div>
                      {c.linkedin_url && (
                        <a
                          href={c.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`warm-intro-linkedin-${c.lead_id}`}
                        >
                          <ExternalLink
                            className="h-3.5 w-3.5"
                            strokeWidth={1.5}
                          />
                        </a>
                      )}
                      {status === "sent" && (
                        <Badge
                          variant="outline"
                          className="text-xs border-green-600/40 text-green-700"
                        >
                          <Check
                            className="h-3 w-3 mr-1"
                            strokeWidth={2}
                          />
                          Intro sent
                        </Badge>
                      )}
                      {status === "skipped" && (
                        <Badge
                          variant="outline"
                          className="text-xs text-muted-foreground"
                        >
                          Skipped
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {[c.title, c.company, c.location, c.industry]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                    <div className="mt-2 text-xs">
                      <span className="text-muted-foreground">Mutuals: </span>
                      <span
                        className={
                          c.mutual_count > 0
                            ? "text-foreground font-medium"
                            : "text-muted-foreground"
                        }
                        data-testid={`warm-intro-mutual-count-${c.lead_id}`}
                      >
                        {c.mutual_count}
                      </span>
                      {c.mutual_count > 0 && c.top_reply_score > 0 && (
                        <span
                          className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium ${
                            c.top_reply_band === "strong"
                              ? "bg-emerald-100 text-emerald-800"
                              : c.top_reply_band === "solid"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-slate-100 text-slate-700"
                          }`}
                          style={{ borderRadius: 2 }}
                          data-testid={`warm-intro-reply-score-${c.lead_id}`}
                          title={`Best mutual reply-probability: ${c.top_reply_score}/100`}
                        >
                          {c.top_reply_band?.toUpperCase()} · {c.top_reply_score}
                        </span>
                      )}
                      {c.mutual_count > 0 && (
                        <span className="text-muted-foreground">
                          {" — "}
                          {c.mutuals
                            .slice(0, 3)
                            .map((m) => m.name)
                            .join(", ")}
                          {c.mutual_count > 3 && ` +${c.mutual_count - 3} more`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {status === "pending" && c.mutual_count > 0 && (
                      <Button
                        size="sm"
                        variant={isOpen ? "outline" : "default"}
                        onClick={() =>
                          setSelected(isOpen ? null : c.lead_id)
                        }
                        data-testid={`warm-intro-toggle-${c.lead_id}`}
                      >
                        <Sparkles
                          className="h-3.5 w-3.5 mr-1.5"
                          strokeWidth={1.5}
                        />
                        {isOpen ? "Collapse" : "Review draft"}
                      </Button>
                    )}
                    {status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => skip(c)}
                        data-testid={`warm-intro-skip-${c.lead_id}`}
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && status === "pending" && (
                  <div className="border-t border-border p-4 bg-secondary/30 space-y-3">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5">
                        Route intro through
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {c.mutuals.map((m) => {
                          const active = m.provider_id === d.mutual_provider_id;
                          const bandColor =
                            m.reply_band === "strong"
                              ? "text-emerald-700"
                              : m.reply_band === "solid"
                              ? "text-amber-700"
                              : "text-slate-500";
                          return (
                            <button
                              key={m.provider_id || m.name}
                              onClick={() =>
                                setDraft(c.lead_id, {
                                  mutual_provider_id: m.provider_id,
                                })
                              }
                              data-testid={`warm-intro-mutual-pick-${c.lead_id}-${m.provider_id}`}
                              className={`px-2.5 py-1.5 text-xs border transition-colors text-left ${
                                active
                                  ? "bg-foreground text-background border-foreground"
                                  : "bg-transparent text-foreground border-border hover:bg-secondary/60"
                              }`}
                              style={{ borderRadius: 2 }}
                              title={(m.reply_reasons || []).join(" · ")}
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium">{m.name}</span>
                                {typeof m.reply_score === "number" && (
                                  <span
                                    className={`text-[10px] font-mono ${
                                      active ? "text-background/80" : bandColor
                                    }`}
                                  >
                                    {m.reply_score}
                                  </span>
                                )}
                              </div>
                              <div
                                className={`text-[10px] ${
                                  active
                                    ? "text-background/70"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {(m.headline || m.company || "").slice(0, 60)}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                        <Sparkles
                          className="h-3 w-3"
                          strokeWidth={1.5}
                        />
                        AI-drafted DM to{" "}
                        <span className="font-medium text-foreground">
                          {chosenMutual?.name || "the mutual"}
                        </span>
                      </div>
                      <Textarea
                        value={d.message || ""}
                        onChange={(e) =>
                          setDraft(c.lead_id, { message: e.target.value })
                        }
                        rows={5}
                        className="text-sm"
                        data-testid={`warm-intro-message-${c.lead_id}`}
                      />
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelected(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => sendIntro(c)}
                        disabled={sendingId === c.lead_id}
                        data-testid={`warm-intro-send-${c.lead_id}`}
                      >
                        <Send
                          className="h-3.5 w-3.5 mr-1.5"
                          strokeWidth={1.5}
                        />
                        {sendingId === c.lead_id ? "Sending…" : "Send DM"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
